/**
 * Template Preview Component
 *
 * Shows expanded scopes for a capability template.
 * Displays which apps are included and which are skipped
 * based on workspace entitlements.
 */

import { useState, useEffect } from 'react';
import { TemplateExpandResponse } from '../types/api';

interface TemplatePreviewProps {
  workspaceId: string;
  templateId: string;
  customScopes?: string[];
  showSkippedApps?: boolean;
}

// App display names for better UX
const appDisplayNames: Record<string, string> = {
  'crm': 'CRM',
  outbound: 'Outbound',
  vitals: 'Vitals',
  proactive: 'Proactive',
  central: 'Central',
  'live-chat': 'Live Chat',
  'help-desk': 'Help Desk',
};

// Scope action display names
const actionLabels: Record<string, string> = {
  read: 'View',
  write: 'Edit',
  delete: 'Delete',
  admin: 'Admin',
  manage: 'Manage',
  export: 'Export',
  import: 'Import',
};

function formatScope(scope: string): { app: string; domain: string; action: string } {
  const parts = scope.split(':');
  if (parts.length >= 3) {
    return {
      app: parts[0],
      domain: parts[1],
      action: parts[2],
    };
  }
  return { app: scope, domain: '', action: '' };
}

function formatDomain(domain: string): string {
  return domain
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function TemplatePreview({
  workspaceId,
  templateId,
  customScopes = [],
  showSkippedApps = true,
}: TemplatePreviewProps) {
  const [expandResult, setExpandResult] = useState<TemplateExpandResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExpansion();
  }, [workspaceId, templateId, JSON.stringify(customScopes)]);

  async function loadExpansion() {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/capability-templates/${templateId}/expand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customScopes }),
      });

      if (response.ok) {
        const data = await response.json();
        setExpandResult(data);
      } else {
        setError('Failed to load template preview');
      }
    } catch {
      setError('Failed to load template preview');
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="py-4 text-center">
        <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-xs text-gray-500 mt-2">Loading permissions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!expandResult) {
    return null;
  }

  // Group scopes by app
  const scopesByApp: Record<string, Array<{ domain: string; action: string }>> = {};
  for (const scope of expandResult.expandedScopes) {
    const { app, domain, action } = formatScope(scope);
    if (!scopesByApp[app]) {
      scopesByApp[app] = [];
    }
    scopesByApp[app].push({ domain, action });
  }

  const hasScopes = expandResult.expandedScopes.length > 0;
  const hasSkippedApps = expandResult.skippedApps.length > 0;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-700">
            {expandResult.expandedScopes.length} permissions
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          <span className="text-gray-700">
            {expandResult.filteredApps.length} apps
          </span>
        </div>
      </div>

      {/* Scopes by App */}
      {hasScopes && (
        <div className="space-y-3">
          {Object.entries(scopesByApp).map(([appId, scopes]) => (
            <div key={appId} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                <span className="font-medium text-gray-900 text-sm">
                  {appDisplayNames[appId] || appId}
                </span>
              </div>
              <div className="p-3">
                <div className="flex flex-wrap gap-2">
                  {scopes.map(({ domain, action }, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                    >
                      <span className="font-medium">{formatDomain(domain)}</span>
                      <span className="mx-1 text-gray-400">&middot;</span>
                      <span className="text-gray-500">{actionLabels[action] || action}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!hasScopes && (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <svg className="w-10 h-10 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p className="text-sm text-gray-500 mt-2">
            No permissions will be granted
          </p>
          <p className="text-xs text-gray-400 mt-1">
            The apps in this template are not enabled for your workspace
          </p>
        </div>
      )}

      {/* Skipped Apps Warning */}
      {showSkippedApps && hasSkippedApps && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex gap-2">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-800">
                Some apps not available
              </p>
              <p className="text-xs text-amber-700 mt-1">
                The following apps in this template are not enabled for your workspace:{' '}
                <span className="font-medium">
                  {expandResult.skippedApps.map((app) => appDisplayNames[app] || app).join(', ')}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
